import { lazily } from "react-lazily";
import { Navigate, useLoaderData } from "react-router";

import { paths } from "app/constants/paths";
import { useRouteParams } from "app/hooks/routing/useRouteParams";
import { lazyRouteLoader } from "app/utils/lazyRouteLoader";

import { DashboardProviderGuard } from "./components/DashboardProviderGuard";
import { CostDashboardBarStateProvider } from "./contexts/CostDashboardBarContext";
import { isCisDashboardKey, isCostDashboardKey } from "./utils/dashboardKeys";

import type {
  DashboardDetailsPreload,
  DashboardPreload,
} from "./dashboardPreload";
import type { PreloadingRouteProps } from "app/utils/lazyRouteLoader";

// Routes.tsx imports this module statically, so it is part of the app shell's
// bundle: the pages and their preload module may only be imported dynamically
// here, or the split that lets the loaders run ahead of the pages' chunks is
// lost. (DashboardProviderGuard and CostDashboardBarStateProvider were already
// in the shell.)
//
// No Suspense boundary of their own: the pages fall back to App's, as they did
// before they preloaded.

const { ComplianceDashboard } = lazily(
  () => import("./components/ComplianceDashboard"),
);
const { CostDashboard } = lazily(() => import("./cost/CostDashboard"));
const { SectionDetails } = lazily(() => import("./details/SectionDetails"));
const { OpportunityDetails } = lazily(
  () => import("./cost/details/OpportunityDetails"),
);

/**
 * The /dashboards/:key route's element: a cost dashboard behind its provider
 * guard, or a compliance one, each handed what the loader preloaded.
 *
 * The pages are keyed on the dashboard, so moving to another one remounts the
 * page: the query reference it renders from belongs to the dashboard the
 * loader ran for.
 */
function DashboardRoute() {
  const preloaded = useLoaderData<DashboardPreload | null>();
  const { key = "" } = useRouteParams(paths.dashboard);

  if (isCisDashboardKey(key)) {
    return <Navigate to={`${paths.dashboardsCis}?version=${key}`} replace />;
  }

  if (isCostDashboardKey(key)) {
    return (
      <DashboardProviderGuard dashboardKey={key} preloaded={preloaded?.guard}>
        <CostDashboardBarStateProvider>
          <CostDashboard key={key} preloaded={preloaded?.cost} />
        </CostDashboardBarStateProvider>
      </DashboardProviderGuard>
    );
  }

  return <ComplianceDashboard key={key} preloaded={preloaded?.compliance} />;
}

/**
 * The /dashboards/:key/details/:sectionKey route's element: the details
 * counterpart of DashboardRoute, for a cost dashboard's opportunity or a
 * compliance (or CIS) dashboard's section.
 */
function DashboardDetailsRoute() {
  const preloaded = useLoaderData<DashboardDetailsPreload | null>();
  const { key = "", sectionKey = "" } = useRouteParams(
    paths.dashboardSectionDetails,
  );
  const pageKey = `${key}/${sectionKey}`;

  if (isCostDashboardKey(key)) {
    return (
      <DashboardProviderGuard dashboardKey={key} preloaded={preloaded?.guard}>
        <CostDashboardBarStateProvider hasSelectedOpportunity>
          <OpportunityDetails
            key={pageKey}
            preloaded={preloaded?.opportunity}
          />
        </CostDashboardBarStateProvider>
      </DashboardProviderGuard>
    );
  }

  return <SectionDetails key={pageKey} preloaded={preloaded?.section} />;
}

/** The /dashboards/:key route: see DashboardRoute. */
export const dashboardRoute: PreloadingRouteProps = {
  element: <DashboardRoute />,
  loader: lazyRouteLoader({
    load: () =>
      import("./dashboardPreload").then((module) =>
        module.createDashboardLoader(),
      ),
    // Only the page the key renders; a CIS key redirects, so has none.
    warm: ({ params }) => {
      const key = params.key ?? "";

      if (isCisDashboardKey(key)) {
        return Promise.resolve();
      }

      return isCostDashboardKey(key)
        ? import("./cost/CostDashboard")
        : import("./components/ComplianceDashboard");
    },
  }),
  // The pages keep their own queries up to date while they are mounted (see
  // useRoutePreloadedQuery), so the loader only runs again when the URL names
  // a different dashboard — not for the filter and tab changes the cost
  // dashboard writes to the URL, which it handles itself.
  shouldRevalidate: ({ currentParams, nextParams }) =>
    currentParams.key !== nextParams.key,
};

/** The /dashboards/:key/details/:sectionKey route: see DashboardDetailsRoute. */
export const dashboardDetailsRoute: PreloadingRouteProps = {
  element: <DashboardDetailsRoute />,
  loader: lazyRouteLoader({
    load: () =>
      import("./dashboardPreload").then((module) =>
        module.createDashboardDetailsLoader(),
      ),
    warm: ({ params }) =>
      isCostDashboardKey(params.key ?? "")
        ? import("./cost/details/OpportunityDetails")
        : import("./details/SectionDetails"),
  }),
  // As for dashboardRoute, keyed on the section as well as the dashboard.
  shouldRevalidate: ({ currentParams, nextParams }) =>
    currentParams.key !== nextParams.key ||
    currentParams.sectionKey !== nextParams.sectionKey,
};
